import React, { ReactNode, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

interface PageTransitionProps {
  children: ReactNode;
}

// Navigation path order used to determine transition direction (still useful for timing)
const navigationOrder = ['/login', '/dashboard', '/inventory', '/repairs', '/transactions', '/reports', '/profile'];

/**
 * PageTransition component adds smooth transitions between route changes.
 * Now uses subtle scale/shift animations for a more elegant feel.
 */
const PageTransition: React.FC<PageTransitionProps> = ({ children }) => {
  const location = useLocation();
  const [displayLocation, setDisplayLocation] = useState(location);
  // Initialize with 'in-fade' for the very first page load to avoid an initial "out" animation.
  const [transitionStage, setTransitionStage] = useState('in-fade');
  
  useEffect(() => {
    if (location.pathname !== displayLocation.pathname) {
      // Determine a generic 'out' or 'fade-out' stage.
      // The actual animation (subtleScaleOut or fadeOut) will be decided by getAnimationClass.
      // We still use the concept of "direction" from navigationOrder to decide if it's a
      // primary navigation flow (using subtleScale) or a fallback (using fade).
      const currentIndex = navigationOrder.indexOf(displayLocation.pathname);
      const nextIndex = navigationOrder.indexOf(location.pathname);
      let transitionType = 'fade'; // Default to fade
      if (currentIndex !== -1 && nextIndex !== -1) {
        transitionType = 'subtle'; // Use subtle scale for main navigation paths
      }
      
      setTransitionStage('out-' + transitionType);
      
      setTimeout(() => {
        setDisplayLocation(location);
        setTransitionStage('in-' + transitionType);
      }, 200); // Matches 0.2s animation duration
    } else if (displayLocation.pathname === location.pathname && transitionStage !== 'in-fade' && transitionStage !== 'in-subtle') {
      // This handles the initial load case correctly if not already in an "in" state.
      // If it's the first load, and we didn't set an initial 'in-fade', this would apply it.
      // However, with initial state as 'in-fade', this part is less critical but good for robustness.
      setTransitionStage('in-fade');
    }
  }, [location, displayLocation, transitionStage]);

  const getAnimationClass = () => {
    switch (transitionStage) {
      // "subtle" transitions use the new scale/shift animations
      case 'in-subtle':
        return 'animate-subtleScaleIn';
      case 'out-subtle':
        return 'animate-subtleScaleOut';
      
      // "fade" transitions are the fallback
      case 'in-fade':
        return 'animate-fadeIn';
      case 'out-fade':
        return 'animate-fadeOut';
        
      default:
        // Fallback for any unhandled or initial state, usually the first page load
        return 'animate-fadeIn';
    }
  };

  return (
    <div
      key={displayLocation.pathname}
      className={`w-full h-full ${getAnimationClass()}`}
    >
      {children}
    </div>
  );
};

export default PageTransition; 